import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, ShieldAlert, KeyRound, Copy, Download } from "lucide-react";

// Modular banner — threshold + tone driven by props, backend defaults respected.
export default function RecoveryCodesBanner({ threshold = 2 }) {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [otp, setOtp] = useState("");
  const [newCodes, setNewCodes] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/auth/mfa/status");
      setStatus(data);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  if (!status || !status.enabled) return null;
  const remaining = status.remaining_codes ?? 0;
  const th = status.low_warning_threshold ?? threshold;
  if (remaining > th) return null;

  const isCritical = remaining === 0;
  const regen = async () => {
    if (!otp || otp.length < 6) { toast.error("Enter your current 6-digit code"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/regenerate-codes", { otp });
      setNewCodes(data.recovery_codes || []);
      setOtp("");
      toast.success("New recovery codes generated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    } finally { setBusy(false); }
  };

  const closeAll = () => { setOpen(false); setNewCodes(null); setOtp(""); };
  const copyAll = () => { navigator.clipboard.writeText((newCodes || []).join("\n")); toast.success("All codes copied"); };
  const downloadTxt = () => {
    const blob = new Blob([(newCodes || []).join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `mfa-recovery-codes-${Date.now()}.txt`; a.click();
    URL.revokeObjectURL(url);
  };

  const wrap = isCritical
    ? "border-red-500/50 bg-red-500/10 text-red-200"
    : "border-amber-400/50 bg-amber-400/10 text-amber-200";
  const Icon = isCritical ? ShieldAlert : AlertTriangle;
  const headline = isCritical
    ? "No recovery codes remaining. Generate new codes immediately."
    : `You have ${remaining} recovery code${remaining === 1 ? "" : "s"} left. Generate new codes now to avoid getting locked out.`;

  return (
    <>
      <div className={`rounded-2xl border ${wrap} p-4 flex items-start gap-3`} data-testid="recovery-codes-banner">
        <Icon className={`w-5 h-5 mt-0.5 ${isCritical ? "text-red-400" : "text-amber-400"}`} />
        <div className="flex-1">
          <div className="text-sm font-semibold">{headline}</div>
          <div className="text-xs opacity-70 mt-0.5">Recovery codes are single-use and let you sign in if you lose your authenticator app.</div>
        </div>
        <Button
          onClick={() => setOpen(true)}
          className={isCritical ? "bg-red-500 hover:bg-red-600 text-white" : "btn-gold"}
          size="sm"
          data-testid="recovery-codes-generate-btn"
        >
          <KeyRound className="w-3.5 h-3.5 mr-2" /> Generate new codes
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(v) => { if (!v) closeAll(); else setOpen(true); }}>
        <DialogContent className="bg-[#111827] border-white/10 text-white" data-testid="recovery-codes-dialog">
          <DialogHeader><DialogTitle className="font-display">Generate new recovery codes</DialogTitle></DialogHeader>
          {!newCodes ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-400">This invalidates all previous codes. Confirm with a current authenticator code to continue.</p>
              <div>
                <Label className="text-xs uppercase text-slate-400">6-digit code</Label>
                <Input
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  maxLength={6}
                  className="bg-black/30 border-white/10 h-11 mt-1 font-mono text-center text-lg tracking-widest"
                  data-testid="recovery-codes-otp"
                />
              </div>
              <Button onClick={regen} disabled={busy} className="btn-gold w-full" data-testid="recovery-codes-confirm">
                {busy ? "Generating…" : "Invalidate & generate new codes"}
              </Button>
            </div>
          ) : (
            <div className="space-y-4" data-testid="recovery-codes-fresh-list">
              <div className="text-amber-400 text-sm font-bold flex items-center gap-2">
                <KeyRound className="w-4 h-4" /> Save these codes — they won't be shown again
              </div>
              <div className="grid grid-cols-2 gap-2 font-mono text-sm">
                {newCodes.map((c, i) => (
                  <div key={i} className="bg-black/40 border border-amber-400/20 rounded px-3 py-2 text-amber-300 tracking-wider">{c}</div>
                ))}
              </div>
              <div className="flex gap-2">
                <Button onClick={copyAll} variant="outline" className="bg-transparent border-amber-400/40 text-amber-400 flex-1"><Copy className="w-3.5 h-3.5 mr-2" /> Copy all</Button>
                <Button onClick={downloadTxt} variant="outline" className="bg-transparent border-amber-400/40 text-amber-400 flex-1"><Download className="w-3.5 h-3.5 mr-2" /> Download .txt</Button>
              </div>
              <Button onClick={closeAll} className="btn-gold w-full" data-testid="recovery-codes-done">I've saved them</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
